import { User } from "../types";
import { ACTIONS, RESOURCES } from "../constants/permissions.constants";

export const SYSTEM_ROLES = ['Account Owner', 'Admin', 'Director', 'Senior Manager', 'Manager'];
export const FIELD_ROLES = ['Supervisor', 'Team Lead', 'Field Worker', 'Worker'];

/**
 * Checks if the target user is the same as the current logged-in user.
 */
export const isSelf = (targetId: string | number | undefined, currentId: string | number | undefined): boolean => {
  if (!targetId || !currentId) return false;
  return String(targetId) === String(currentId);
};

/**
 * Checks if a role is a global administrative role (Admin/Director).
 */
export const isGlobalRole = (role?: string): boolean => {
  if (!role) return false;
  return ['Admin', 'Director'].includes(role);
};

/**
 * Checks if a role is the Account Owner role.
 */
export const isOwnerRole = (role?: string): boolean => {
  return role === 'Account Owner';
};

/**
 * Checks if a role is a global management role (Senior Manager/Manager).
 */
export const isGlobalManagerRole = (role?: string): boolean => {
  if (!role) return false;
  return ['Senior Manager', 'Manager'].includes(role);
};

/**
 * Checks if a role is a management role (Hierarchy oversight).
 * Includes both global managers and local supervisors/team leads.
 */
export const isManagementRole = (role?: string): boolean => {
  if (!role) return false;
  return isGlobalManagerRole(role) || ['Supervisor', 'Team Lead'].includes(role);
};

/**
 * Complex determination of whether a user is a Global Admin with specific ABAC rights to individuals.
 * This is used to bypass hierarchy checks in specific modules.
 */
export const hasGlobalPersonAccess = (currentUser: User | null, ability: any): boolean => {
  if (!currentUser) return false;

  const hasGlobalPrimaryRole = isGlobalRole(currentUser.userRole) || isOwnerRole(currentUser.userRole);
  return hasGlobalPrimaryRole && ability.can(ACTIONS.READ, RESOURCES.PERSON_DETAIL_PAGE);
};

/**
 * Centralized logic to determine if the profile link/details should be visible.
 * This combines Identity, Global Permissions, and Hierarchical Managed status.
 */
export const canViewProfile = (
  targetId: string | number | undefined,
  currentUser: User | null,
  ability: any,
  canManageUser?: (id: string | number | undefined) => boolean
): boolean => {
  if (!targetId || !currentUser) return false;

  // 1. Can always view own profile
  if (isSelf(targetId, currentUser?.id)) return true;

  // 2. Global Admins (Admin/Director/Owner) with explicit READ permission can view all
  if (hasGlobalPersonAccess(currentUser, ability)) return true;

  // 3. Managers/Supervisors can ONLY view if they pass the recursive hierarchy check 
  // AND have the "Managed Page" permission.
  const canReadManaged = ability.can(ACTIONS.READ, RESOURCES.PERSON_I_MANAGE_PAGE);
  if (canManageUser?.(targetId) && canReadManaged) {
    return true;
  }

  return false;
};
